import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add,
  MoreVert,
  Delete,
  DeleteForever,
  Visibility,
  Refresh,
  Sync,
  ContentCopy,
  CheckCircle,
  ViewModule,
  ViewList,
  FileDownload,
  Update
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { Tenant, TenantCreate, TenantStatus, Size } from '../types';
import { CustomerSummary } from '../types/customer';
import StatusChip from '../components/StatusChip';
import LoadingButton from '../components/LoadingButton';

const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<TenantStatus | ''>('');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [refreshingVersions, setRefreshingVersions] = useState(false);

  // Create tenant dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createData, setCreateData] = useState<TenantCreate>({
    name: '',
    contact_email: '',
    customer_id: 0,
    size_id: 0, // Will be set to first available size when sizes are loaded
    admin_email: '', // Email for the tenant administrator account
    description: ''
    // Removed: questions_per_month, max_files_per_project, max_projects, max_streams_selection, max_users
    // These values now come automatically from the selected Size
  });

  // Customer data for tenant creation
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Size data for tenant creation
  const [sizes, setSizes] = useState<Size[]>([]);
  const [loadingSizes, setLoadingSizes] = useState(false);

  // Action menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  // Admin credentials dialog
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<{
    tenant_name: string;
    admin_email: string;
    admin_password: string;
    tenant_url: string;
  } | null>(null);

  const loadTenants = async () => {
    try {
      setError('');
      // En modo LISTA traemos un lote grande y el DataGrid pagina/filtra/ordena en
      // cliente sobre todo el conjunto; en TARJETA mantenemos la paginación de servidor.
      const response = await apiService.getTenants({
        page: viewMode === 'list' ? 1 : page,
        per_page: viewMode === 'list' ? 1000 : 12,
        status: statusFilter || undefined
      });

      setTenants(response.tenants);
      setTotalPages(response.pages);
    } catch (err: any) {
      console.error('Failed to load tenants:', err);
      setError(err.detail || 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
              const customerOptions = await apiService.getCustomerOptions();
      setCustomers(customerOptions);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadSizes = async () => {
    try {
      setLoadingSizes(true);
      const response = await apiService.getActiveSizes();
      setSizes(response.sizes);
      
      // Auto-select first available size if no size is selected
      if (response.sizes.length > 0 && createData.size_id === 0) {
        setCreateData(prev => ({
          ...prev,
          size_id: response.sizes[0].id
        }));
      }
    } catch (error) {
      console.error('Error loading sizes:', error);
      toast.error('Error loading sizes');
    } finally {
      setLoadingSizes(false);
    }
  };

  const handleCreateTenant = async () => {
    try {
      setCreateLoading(true);
      const response = await apiService.createTenant(createData);
      
      // Store generated credentials
      setGeneratedCredentials({
        tenant_name: response.name,
        admin_email: response.admin_email || createData.admin_email,
        admin_password: response.admin_password || '',
        tenant_url: `https://${response.name}.usewai.com`
      });
      
      toast.success(t('tenant.tenantCreated'));
      setCreateDialogOpen(false);
      
      // Show credentials dialog
      setCredentialsDialogOpen(true);
      
      // Reset form with first available size_id
      const defaultSizeId = sizes.length > 0 ? sizes[0].id : 0;
      setCreateData({ 
        name: '', 
        contact_email: '', 
        customer_id: 0, 
        size_id: defaultSizeId, 
        admin_email: '',
        description: ''
      });
      
      loadTenants();
    } catch (err: any) {
      console.error('Failed to create tenant:', err);
      toast.error(err.detail || t('messages.errorCreatingTenant'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!window.confirm(`Are you sure you want to delete tenant "${tenant.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteTenant(tenant.id);
      toast.success(t('tenant.tenantDeleted'));
      loadTenants();
    } catch (err: any) {
      console.error('Failed to delete tenant:', err);
      toast.error(err.detail || t('messages.errorDeletingTenant'));
    }
  };

  const handleForceDeleteTenant = async (tenant: Tenant) => {
    if (!window.confirm(`⚠️ FORCE DELETE tenant "${tenant.name}"?\n\nThis will:\n• Cancel any running deployment jobs\n• Delete tenant regardless of status\n• Cannot be undone\n\nContinue?`)) {
      return;
    }

    try {
      await apiService.forceDeleteTenant(tenant.id);
      toast.success('Tenant FORCE deletion started (ignoring status)');
      loadTenants();
    } catch (err: any) {
      console.error('Failed to force delete tenant:', err);
      toast.error(err.detail || 'Failed to force delete tenant');
    }
  };

  const handleCleanTenant = async (tenant: Tenant) => {
    if (!window.confirm(`🗑️ CLEAN tenant "${tenant.name}" from database?\n\nThis will:\n• Permanently remove tenant from database\n• Cannot be undone\n• Only works if tenant is already DELETED\n\nContinue?`)) {
      return;
    }

    try {
      await apiService.cleanTenant(tenant.id);
      toast.success(`Tenant "${tenant.name}" cleaned from database`);
      loadTenants();
    } catch (err: any) {
      console.error('Failed to clean tenant:', err);
      toast.error(err.detail || 'Failed to clean tenant');
    }
  };

  const handleRecreateTenant = async (tenant: Tenant) => {
    if (!window.confirm(`🔄 RECREATE tenant "${tenant.name}"?\n\nThis will:\n• Start a new deployment job\n• Attempt to create the tenant again\n• Previous failed deployment will be cleared\n\nContinue?`)) {
      return;
    }

    try {
      // First, try to create the tenant again (this should restart the deployment)
      await apiService.createTenant({
        name: tenant.name,
        contact_email: tenant.contact_email,
        customer_id: tenant.customer_id,
        size_id: tenant.size_id,
        admin_email: tenant.contact_email, // Use contact_email as default for recreation
        description: tenant.description || ''
      });
      toast.success(`Tenant "${tenant.name}" recreation started`);
      loadTenants();
    } catch (err: any) {
      console.error('Failed to recreate tenant:', err);
      toast.error(err.detail || 'Failed to recreate tenant. Try force delete first.');
    }
  };

  // Helper function to determine available actions for a tenant based on its status
  const getAvailableActions = (tenant: Tenant) => {
    const actions = {
      viewDetails: true, // Always available
      syncStatus: false, // Only in creating/deleting
      delete: false,
      forceDelete: false,
      clean: false,
      recreate: false
    };

    switch (tenant.status.toUpperCase()) {
      case 'CREATING':
        // During creation, allow sync and force delete if stuck
        actions.syncStatus = true;
        actions.forceDelete = true;
        break;
      
      case 'ACTIVE':
        // Active tenants can only be deleted normally (no force delete, no sync)
        actions.delete = true;
        break;
      
      case 'DELETING':
        // Deleting tenants can be synced and force deleted if stuck
        actions.syncStatus = true;
        actions.forceDelete = true;
        break;
      
      case 'DELETED':
        // Deleted tenants can only be cleaned from database
        actions.clean = true;
        break;
      
      case 'FAILED':
        // Failed tenants can be deleted or recreated (no force delete needed)
        actions.delete = true;
        actions.recreate = true;
        break;
      
      default:
        // Unknown status - allow recreate as fallback
        actions.recreate = true;
        break;
    }

    return actions;
  };

  const handleSyncTenant = async (tenant: Tenant) => {
    try {
      await apiService.syncTenantStatus(tenant.id);
      toast.success(t('messages.tenantStatusSynced'));
      loadTenants();
    } catch (err: any) {
      console.error('Failed to sync tenant:', err);
      toast.error(err.detail || t('messages.tenantSyncFailed'));
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, tenant: Tenant) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedTenant(null);
  };

  const isValidTenantName = (name: string) => {
    return /^[a-z0-9-]+$/.test(name) && name.length >= 3 && name.length <= 30;
  };

  useEffect(() => {
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, viewMode]);

  // Clientes y tallas se cargan siempre (no solo al abrir el diálogo de alta): la
  // lista y el export necesitan resolver customer_id/size_id → nombre.
  useEffect(() => {
    loadCustomers();
    loadSizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customerName = (id: number) => customers.find((c) => c.id === id)?.name || `#${id}`;
  const sizeName = (id: number) => sizes.find((s) => s.id === id)?.display_name || `#${id}`;

  const handleRefreshVersion = async (tenant: Tenant) => {
    try {
      const updated = await apiService.refreshTenantVersion(tenant.id);
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? { ...t, ...updated } : t)));
      toast.success(t('tenant.versionRefreshed', 'Versión actualizada'));
    } catch (err: any) {
      toast.error(err.detail || t('tenant.versionRefreshFailed', 'No se pudo leer la versión del tenant'));
    }
  };

  const handleRefreshAllVersions = async () => {
    try {
      setRefreshingVersions(true);
      const res = await apiService.refreshAllTenantVersions();
      toast.success(
        t('tenant.versionsRefreshedSummary', 'Versiones actualizadas: {{updated}} · sin dato: {{unavailable}}')
          .replace('{{updated}}', String(res.updated))
          .replace('{{unavailable}}', String(res.unavailable))
      );
      loadTenants();
    } catch (err: any) {
      toast.error(err.detail || t('tenant.versionRefreshFailed', 'No se pudieron actualizar las versiones'));
    } finally {
      setRefreshingVersions(false);
    }
  };

  // Aplana TODOS los campos del tenant a una fila de Excel (no solo lo visible).
  const tenantToRow = (tenant: Tenant) => ({
    id: tenant.id,
    name: tenant.name,
    status: tenant.status,
    contact_email: tenant.contact_email,
    description: tenant.description || '',
    domain: tenant.domain || '',
    cliente: customerName(tenant.customer_id),
    talla: sizeName(tenant.size_id),
    owner_id: tenant.owner_id,
    db_schema_version: tenant.db_schema_version || '',
    db_schema_version_n: tenant.db_schema_version_n ?? '',
    db_schema_source: tenant.db_schema_sealed == null ? '' : tenant.db_schema_sealed ? 'sellada' : 'inferida',
    deploy_version: tenant.deploy_version || '',
    version_checked_at: tenant.version_checked_at || '',
    last_deployment_op: tenant.latest_deployment?.operation_type || '',
    last_deployment_status: tenant.latest_deployment?.status || '',
    created_at: tenant.created_at,
    updated_at: tenant.updated_at,
    deleted_at: tenant.deleted_at || '',
  });

  const handleExportExcel = () => {
    const rows = tenants.map(tenantToRow);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tenants');
    const stamp = format(new Date(), 'yyyyMMdd-HHmm');
    XLSX.writeFile(wb, `tenants-${stamp}.xlsx`);
  };

  // Chip de esquema: 'V69 · sellada' (verde) | 'V69 · inferida' (ámbar) | '—' sin dato.
  const renderSchemaChip = (tenant: Tenant) => {
    if (!tenant.db_schema_version) {
      return <Chip label={t('tenant.versionUnknown', 'BD: —')} size="small" variant="outlined" />;
    }
    const sealed = tenant.db_schema_sealed;
    const source = sealed
      ? t('tenant.versionSealed', 'sellada')
      : t('tenant.versionInferred', 'inferida');
    return (
      <Chip
        label={`BD: ${tenant.db_schema_version} · ${source}`}
        size="small"
        color={sealed ? 'success' : 'warning'}
        variant={sealed ? 'filled' : 'outlined'}
      />
    );
  };

  const renderDeployChip = (tenant: Tenant) =>
    tenant.deploy_version ? (
      <Chip label={`Deploy: ${tenant.deploy_version}`} size="small" variant="outlined" color="info" />
    ) : (
      <Chip label={t('tenant.deployUnknown', 'Deploy: —')} size="small" variant="outlined" />
    );

  const tenantColumns: GridColDef[] = [
    { field: 'name', headerName: t('tenant.tenantName', 'Nombre'), flex: 1, minWidth: 140 },
    {
      field: 'status', headerName: t('tenant.status', 'Estado'), width: 120,
      renderCell: (p) => <StatusChip status={(p.row as Tenant).status} size="small" />,
    },
    {
      field: 'db_schema_version_n', headerName: t('tenant.schemaVersion', 'Esquema BD'), width: 190,
      valueGetter: (p) => (p.row as Tenant).db_schema_version_n ?? null,
      renderCell: (p) => renderSchemaChip(p.row as Tenant),
    },
    {
      field: 'deploy_version', headerName: t('tenant.deployVersion', 'Release'), width: 120,
      valueGetter: (p) => (p.row as Tenant).deploy_version || '',
    },
    {
      field: 'version_checked_at', headerName: t('tenant.versionCheckedAtShort', 'Leído'), width: 150,
      valueGetter: (p) => ((p.row as Tenant).version_checked_at ? new Date((p.row as Tenant).version_checked_at as string) : null),
      renderCell: (p) => ((p.row as Tenant).version_checked_at ? format(new Date((p.row as Tenant).version_checked_at as string), 'MMM dd HH:mm') : '—'),
    },
    { field: 'contact_email', headerName: t('tenant.contactEmail', 'Email'), flex: 1, minWidth: 180 },
    { field: 'domain', headerName: t('tenant.domain', 'Dominio'), width: 160, valueGetter: (p) => (p.row as Tenant).domain || '' },
    {
      field: 'customer_id', headerName: t('customers.fields.name', 'Cliente'), width: 160,
      valueGetter: (p) => customerName((p.row as Tenant).customer_id),
    },
    {
      field: 'size_id', headerName: t('tenant.size', 'Talla'), width: 130,
      valueGetter: (p) => sizeName((p.row as Tenant).size_id),
    },
    {
      field: 'created_at', headerName: t('tenant.created', 'Creado'), width: 130,
      valueGetter: (p) => new Date((p.row as Tenant).created_at),
      renderCell: (p) => format(new Date((p.row as Tenant).created_at), 'MMM dd, yyyy'),
    },
    {
      field: 'actions', type: 'actions', headerName: '', width: 90,
      getActions: (p) => [
        <GridActionsCellItem key="view" icon={<Visibility />} label={t('tenant.viewDetails', 'Ver')} onClick={() => navigate(`/tenants/${(p.row as Tenant).id}`)} />,
        <GridActionsCellItem key="refresh" icon={<Update />} label={t('tenant.refreshVersion', 'Refrescar versión')} onClick={() => handleRefreshVersion(p.row as Tenant)} />,
      ],
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('tenant.tenants')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
                      onClick={() => {
              setCreateDialogOpen(true);
              loadCustomers();
              loadSizes();
            }}
        >
          {t('tenant.createTenant')}
        </Button>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('tenant.status')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('tenant.status')}
            onChange={(e) => setStatusFilter(e.target.value as TenantStatus | '')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="creating">{t('status.creating')}</MenuItem>
            <MenuItem value="active">{t('status.active')}</MenuItem>
            <MenuItem value="failed">{t('status.failed')}</MenuItem>
            <MenuItem value="deleting">{t('status.deleting')}</MenuItem>
            <MenuItem value="deleted">{t('status.deleted')}</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => loadTenants()}
          size="small"
        >
          {t('common.refresh', 'Refresh')}
        </Button>

        <Box flexGrow={1} />

        <Button
          variant="outlined"
          size="small"
          startIcon={refreshingVersions ? <CircularProgress size={16} /> : <Update />}
          onClick={handleRefreshAllVersions}
          disabled={refreshingVersions}
        >
          {t('tenant.refreshVersions', 'Refrescar versiones')}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownload />}
          onClick={handleExportExcel}
          disabled={tenants.length === 0}
        >
          {t('tenant.exportExcel', 'Exportar Excel')}
        </Button>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v) => { if (v) setViewMode(v); }}
        >
          <ToggleButton value="cards" aria-label="cards">
            <Tooltip title={t('tenant.viewCards', 'Tarjetas')}><ViewModule fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="list">
            <Tooltip title={t('tenant.viewList', 'Lista')}><ViewList fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tenants Grid */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          {t('tenant.loadingTenants')}
        </Box>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              {t('tenant.noTenantsCreate')}
            </Typography>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={tenants}
            getRowId={(r) => r.id}
            columns={tenantColumns}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            slots={{ toolbar: GridToolbar }}
            slotProps={{ toolbar: { showQuickFilter: true } }}
            disableRowSelectionOnClick
          />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {tenants.map((tenant) => (
            <Grid item xs={12} sm={6} md={4} key={tenant.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" mb={2}>
                    <Box flex={1}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {tenant.name}
                      </Typography>
                      <StatusChip status={tenant.status} />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleActionClick(e, tenant)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Typography color="text.secondary" gutterBottom>
                    {tenant.contact_email}
                  </Typography>

                  {tenant.status === 'active' && (
                    <Typography 
                      variant="body2" 
                      color="primary" 
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: 'primary.dark' }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const tenantUrl = `https://${tenant.name}.usewai.com`;
                        window.open(tenantUrl, '_blank');
                      }}
                    >
                      🌐 {t('tenant.access')}: {tenant.name}.usewai.com
                    </Typography>
                  )}

                  {tenant.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {tenant.description}
                    </Typography>
                  )}

                  {tenant.domain && (
                    <Chip 
                      label={tenant.domain} 
                      size="small" 
                      variant="outlined" 
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('tenant.created')}: {format(new Date(tenant.created_at), 'MMM dd, yyyy')}
                  </Typography>

                  <Box display="flex" gap={1} flexWrap="wrap" mt={1.5} alignItems="center">
                    {renderSchemaChip(tenant)}
                    {renderDeployChip(tenant)}
                    <Tooltip title={t('tenant.refreshVersion', 'Refrescar versión')}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRefreshVersion(tenant); }}
                      >
                        <Update fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {tenant.version_checked_at && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {t('tenant.versionCheckedAt', 'Versión leída')}: {format(new Date(tenant.version_checked_at), 'MMM dd HH:mm')}
                    </Typography>
                  )}

                  {tenant.latest_deployment && (
                    <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                      <Typography variant="caption" color="text.secondary">
                        {t('tenant.latestDeployment')}:
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <StatusChip status={tenant.latest_deployment.status} size="small" />
                        <Typography variant="caption" color="text.secondary">
                          {tenant.latest_deployment.operation_type}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination (solo modo tarjeta; en lista pagina el DataGrid) */}
      {viewMode === 'cards' && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => {
          if (selectedTenant) navigate(`/tenants/${selectedTenant.id}`);
          handleActionClose();
        }}>
          <ListItemIcon><Visibility /></ListItemIcon>
          <ListItemText>{t('tenant.viewDetails')}</ListItemText>
        </MenuItem>
        
        {/* Sync Status - only for creating/deleting */}
        {selectedTenant && (() => {
          const actions = getAvailableActions(selectedTenant);
          return actions.syncStatus ? (
            <MenuItem onClick={() => {
              if (selectedTenant) handleSyncTenant(selectedTenant);
              handleActionClose();
            }}>
              <ListItemIcon><Sync /></ListItemIcon>
              <ListItemText>{t('common.syncStatus')}</ListItemText>
            </MenuItem>
          ) : null;
        })()}

        {/* Conditional Actions Based on Tenant Status */}
        {selectedTenant && (() => {
          const actions = getAvailableActions(selectedTenant);
          return (
            <>
              {actions.delete && (
                <MenuItem onClick={() => {
                  if (selectedTenant) handleDeleteTenant(selectedTenant);
                  handleActionClose();
                }}>
                  <ListItemIcon><Delete /></ListItemIcon>
                  <ListItemText>{t('common.delete')}</ListItemText>
                </MenuItem>
              )}

              {actions.forceDelete && (
                <MenuItem 
                  onClick={() => {
                    if (selectedTenant) handleForceDeleteTenant(selectedTenant);
                    handleActionClose();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon sx={{ color: 'error.main' }}><DeleteForever /></ListItemIcon>
                  <ListItemText>{t('tenant.forceDelete')}</ListItemText>
                </MenuItem>
              )}

              {actions.clean && (
                <MenuItem 
                  onClick={() => {
                    if (selectedTenant) handleCleanTenant(selectedTenant);
                    handleActionClose();
                  }}
                  sx={{ color: 'info.main' }}
                >
                  <ListItemIcon sx={{ color: 'info.main' }}><Delete /></ListItemIcon>
                  <ListItemText>{t('tenant.clean')}</ListItemText>
                </MenuItem>
              )}

              {actions.recreate && (
                <MenuItem 
                  onClick={() => {
                    if (selectedTenant) handleRecreateTenant(selectedTenant);
                    handleActionClose();
                  }}
                  sx={{ color: 'success.main' }}
                >
                  <ListItemIcon sx={{ color: 'success.main' }}><Sync /></ListItemIcon>
                  <ListItemText>{t('tenant.recreate')}</ListItemText>
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tenant.createTenant')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('tenant.tenantName')}
            fullWidth
            variant="outlined"
            value={createData.name}
            onChange={(e) => setCreateData({ ...createData, name: e.target.value.toLowerCase() })}
            error={createData.name !== '' && !isValidTenantName(createData.name)}
            helperText={createData.name !== '' && !isValidTenantName(createData.name) 
              ? "Name must be lowercase, 3-30 characters, alphanumeric with hyphens only" 
              : "Lowercase letters, numbers, and hyphens only"}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('customers.fields.name')} *</InputLabel>
            <Select
              value={createData.customer_id}
              onChange={(e) => setCreateData({ ...createData, customer_id: e.target.value as number })}
              label={t('customers.fields.name') + ' *'}
              disabled={loadingCustomers}
            >
              <MenuItem value={0} disabled>
                {loadingCustomers ? 'Cargando clientes...' : 'Seleccione un cliente'}
              </MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('tenant.size')} *</InputLabel>
            <Select
              value={createData.size_id}
              onChange={(e) => setCreateData({ ...createData, size_id: e.target.value as number })}
              label={t('tenant.size') + ' *'}
              disabled={loadingSizes}
            >
              <MenuItem value={0} disabled>
                {loadingSizes ? t('common.loading') : t('tenant.selectSize')}
              </MenuItem>
              {sizes.map((size) => (
                <MenuItem key={size.id} value={size.id}>
                  <Box>
                    <Typography variant="body1">{size.display_name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {size.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            label={t('tenant.contactEmail')}
            type="email"
            fullWidth
            variant="outlined"
            value={createData.contact_email}
            onChange={(e) => setCreateData({ ...createData, contact_email: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Admin Email"
            type="email"
            fullWidth
            variant="outlined"
            required
            value={createData.admin_email}
            onChange={(e) => setCreateData({ ...createData, admin_email: e.target.value })}
            helperText="Email for the tenant administrator account"
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label={t('tenant.description') + ' (' + t('common.optional', { defaultValue: 'Optional' }) + ')'}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={createData.description}
            onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          <LoadingButton
            onClick={handleCreateTenant}
            loading={createLoading}
            disabled={!createData.name || !createData.contact_email || !createData.admin_email || !createData.customer_id || !createData.size_id || !isValidTenantName(createData.name)}
            variant="contained"
          >
            {t('tenant.createTenant')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Admin Credentials Dialog */}
      <Dialog 
        open={credentialsDialogOpen} 
        onClose={() => setCredentialsDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            Tenant Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          {generatedCredentials && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Important: Save these credentials</AlertTitle>
                The password will not be shown again. Please save it securely.
              </Alert>
              
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tenant URL
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {generatedCredentials.tenant_url}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Administrator Email
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {generatedCredentials.admin_email}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Administrator Password
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {generatedCredentials.admin_password}
                  </Typography>
                  <IconButton 
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedCredentials.admin_password);
                      toast.success('Password copied to clipboard');
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                The tenant is being deployed. You can check deployment status in the tenant details page.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCredentialsDialogOpen(false)}
            variant="contained"
          >
            I have saved the credentials
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantsPage;